import { useCallback, useEffect, useState } from 'react';
import { useSearchBox } from 'react-instantsearch';
import { searchClient } from 'utils/algoliaSearchClient';
import { ALGOLIA_INDEX_NAME } from 'config/env';

const contentTypes = ['lernkarte', 'medikamente', 'video', 'searchableImage'];

type SearchTypeCounts = Record<string, number>;

interface UseSearchWithTypeCountsReturn {
  query: string;
  refine: (newQuery: string) => Promise<void>;
  clear: () => void;
  searchTypeCounts: SearchTypeCounts;
  isLoadingCounts: boolean;
}

export const useSearchWithTypeCounts = (): UseSearchWithTypeCountsReturn => {
  const { query, refine: originalRefine, clear } = useSearchBox();
  const [searchTypeCounts, setSearchTypeCounts] = useState<SearchTypeCounts>({});
  const [isLoadingCounts, setIsLoadingCounts] = useState(false);

  const performSearchWithTypeCounts = useCallback(async (searchQuery: string): Promise<void> => {
    if (!searchQuery.trim()) {
      setSearchTypeCounts({});
      return;
    }

    setIsLoadingCounts(true);

    try {
      // Create search requests for each content type
      const requests = contentTypes.map((type) => ({
        indexName: ALGOLIA_INDEX_NAME,
        params: {
          query: searchQuery,
          hitsPerPage: 0, // We only want counts, not actual hits
          filters:
            type === 'medien'
              ? `contentType:"video" OR contentType:"searchableImage"`
              : `contentType:"${type}"`,
          page: 0,
        },
      }));

      const searchResults = await searchClient.search(requests);

      // Extract counts from each result
      const typeCounts: SearchTypeCounts = {};

      searchResults.results.forEach((result, index) => {
        const contentType = contentTypes[index];
        // Use type assertion to access nbHits property
        const searchResult = result as { nbHits?: number };
        if (searchResult.nbHits !== undefined) {
          typeCounts[contentType] = searchResult.nbHits;
        }
      });

      // Add combined medien count (video + searchableImage)
      typeCounts.medien = (typeCounts.video || 0) + (typeCounts.searchableImage || 0);

      setSearchTypeCounts(typeCounts);
    } catch {
      // Handle error silently, reset counts
      setSearchTypeCounts({});
    } finally {
      setIsLoadingCounts(false);
    }
  }, []);

  const refineWithTypeCounts = useCallback(
    async (newQuery: string): Promise<void> => {
      // First, update the instantsearch query
      originalRefine(newQuery);

      // Then, fetch type counts
      await performSearchWithTypeCounts(newQuery);
    },
    [originalRefine, performSearchWithTypeCounts]
  );

  const clearWithTypeCounts = useCallback((): void => {
    clear();
    setSearchTypeCounts({});
  }, [clear]);

  // Initialize type counts when the component mounts with existing query
  useEffect(() => {
    if (query) {
      performSearchWithTypeCounts(query);
    }
  }, [query, performSearchWithTypeCounts]);

  return {
    query,
    refine: refineWithTypeCounts,
    clear: clearWithTypeCounts,
    searchTypeCounts,
    isLoadingCounts,
  };
};
